/**
 * 登入後進入已有 Square：列表 + 輸入後綴
 * 優先使用 LIFF（getIDToken → register → switchSquareForJwt）；非 LIFF 時 fallback 後端 LINE Login。
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBase } from '../../api/apiUtils';
import {
  listMySquares,
  switchSquareForJwt,
  type SquareInfo,
} from '../../api/liteSquaresClient';
import { getLiteJwtToken, setLiteJwtToken } from '../../api/liteAuthToken';
import { registerNewUser } from '../../api/contextClient';
import { useLiff } from '../../contexts/LiffContext';

function base64UrlEncodeUtf8(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function base64UrlEncodeState(state: {
  redirect_uri: string;
  mode: string;
  suffix?: string;
}): string {
  const json = JSON.stringify(state);
  return base64UrlEncodeUtf8(json);
}

/** 導向後端 LINE 登入（mode=enter, suffix=xxx），取得綁定該 square 的 JWT 後導回 /lite/oauth-return（fallback） */
function getEnterSquareAuthUrl(suffix: string): string {
  const apiBase = getApiBase();
  const redirectUri = `${window.location.origin}/lite/oauth-return`;
  const state = base64UrlEncodeState({
    redirect_uri: redirectUri,
    mode: 'enter',
    suffix,
  });
  return `${apiBase}/api/lite/auth/line?redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`;
}

export function SquareEnterPage() {
  const navigate = useNavigate();
  const { liff, isLoggedIn, shouldUseLiff } = useLiff();
  const [squares, setSquares] = useState<SquareInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [suffixInput, setSuffixInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enteringSuffix, setEnteringSuffix] = useState<string | null>(null);

  useEffect(() => {
    const token = getLiteJwtToken();
    if (!token) {
      navigate('/lite/new-square', { replace: true });
      return;
    }
    listMySquares(token)
      .then(setSquares)
      .catch(() => setSquares([]))
      .finally(() => setLoading(false));
  }, [navigate]);

  /** 進入指定 suffix：LIFF 時用 getIDToken + register + switchSquareForJwt，否則 fallback 後端 LINE Login */
  const enterSuffix = async (suffix: string) => {
    const trimmed = suffix.trim().toUpperCase();
    if (!trimmed || trimmed.length < 6 || trimmed.length > 8) {
      setError('請輸入 6–8 碼後綴');
      return;
    }
    setError(null);
    if (shouldUseLiff && liff && isLoggedIn) {
      setEnteringSuffix(trimmed);
      try {
        const lineIdToken = await liff.getIDToken();
        if (!lineIdToken) {
          setError('無法取得 LINE 認證');
          return;
        }
        const result = await registerNewUser({ lineIdToken });
        if (!result.jwtToken) {
          setError('無法取得登入憑證');
          return;
        }
        const newToken = await switchSquareForJwt(result.jwtToken, trimmed);
        setLiteJwtToken(newToken);
        navigate(`/lite/shop/${trimmed}`, { replace: true });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : '進入商家失敗，請稍後再試'
        );
      } finally {
        setEnteringSuffix(null);
      }
    } else {
      window.location.href = getEnterSquareAuthUrl(trimmed);
    }
  };

  const handleEnterBySuffix = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const suffix = suffixInput.trim().toUpperCase();
    if (!suffix || suffix.length < 6 || suffix.length > 8) {
      setError('請輸入 6–8 碼後綴');
      return;
    }
    enterSuffix(suffix);
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <p className="text-slate-600">載入中...</p>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-lg p-6">
        <h1 className="text-xl font-semibold text-slate-800 text-center mb-2">
          進入已有 Square
        </h1>
        <p className="text-sm text-slate-500 text-center mb-6">
          從列表選擇或輸入 6 碼後綴
        </p>

        {squares.length > 0 && (
          <ul className="space-y-2 mb-6">
            {squares.map(sq => (
              <li key={sq.id}>
                <button
                  type="button"
                  onClick={() => enterSuffix(sq.suffix)}
                  disabled={enteringSuffix !== null}
                  className="w-full text-left block rounded-lg border border-slate-200 p-3 text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                >
                  <span className="font-medium">{sq.name}</span>
                  <span className="ml-2 text-xs text-slate-500">
                    ({sq.suffix})
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={handleEnterBySuffix} className="mt-4">
          <input
            type="text"
            value={suffixInput}
            onChange={e => setSuffixInput(e.target.value.toUpperCase())}
            placeholder="輸入 6 碼後綴"
            maxLength={8}
            className="w-full rounded-lg border border-slate-200 px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {error && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/lite/square-choice')}
              className="flex-1 rounded-lg border border-slate-300 text-slate-700 py-3 hover:bg-slate-50"
            >
              返回
            </button>
            <button
              type="submit"
              disabled={enteringSuffix !== null}
              className="flex-1 rounded-lg bg-indigo-600 text-white font-medium py-3 hover:bg-indigo-700 disabled:opacity-50"
            >
              {enteringSuffix ? '進入中…' : '進入'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
